/* eslint-disable no-else-return */
// components/payment/PaymentSuccessPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Result, Button, Spin, Typography, Card, Descriptions, Row, Col, Divider } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined } from '@ant-design/icons';
import UilLeft from '@iconscout/react-unicons/icons/uil-arrow-left';
import { Main } from '../../container/styled';
import { PageHeader } from '../page-headers/page-headers';
import { PesapalService } from '../../services/pesapalService';

const { Text, Title } = Typography;

const PageRoutes = [
  {
    path: 'index',
    breadcrumbName: 'Dashboard',
  },
  {
    path: 'payments',
    breadcrumbName: 'Payments',
  },
  {
    path: '',
    breadcrumbName: 'Payment Result',
  },
];

function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentPath = window.location.pathname;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);

  // Handle both lowercase and uppercase parameter variants
  const orderTrackingId = searchParams.get('orderTrackingId') || searchParams.get('OrderTrackingId');
  const transactionId = searchParams.get('transactionId');
  const investmentId = searchParams.get('investmentId');
  const borrowerId = searchParams.get('borrowerId');
  const type = searchParams.get('type');

  // Determine if user is an investor or borrower based on URL path
  const isInvestor = currentPath.includes('/investor/');
  const isBorrower = currentPath.includes('/borrower/');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!orderTrackingId || !transactionId) {
        setError('Missing payment information. Please check your URL parameters.');
        setLoading(false);
        return;
      }

      try {
        // Use the PesapalService instead of direct fetch
        const { data, error: apiError } = await PesapalService.verifyPayment(orderTrackingId, transactionId);

        if (apiError) {
          throw new Error(apiError);
        }

        setPaymentResult(data);
      } catch (err) {
        console.error('Payment verification error:', err);
        setError(err.message || 'An error occurred while verifying the payment.');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [orderTrackingId, transactionId]);

  const goToDashboard = () => {
    if (isInvestor) {
      navigate('/investor/dashboard');
    } else if (isBorrower) {
      navigate('/borrower');
    } else {
      // Default fallback
      navigate('/dashboard');
    }
  };

  const goToDetails = () => {
    if (isInvestor && type === 'investment' && investmentId) {
      navigate(`/investor/investments/${investmentId}`);
    } else if (isInvestor) {
      navigate('/investor/investments/portfolio');
    } else if (isBorrower && type === 'deposit' && borrowerId) {
      navigate(`/borrower/wallet/details`);
    } else if (isBorrower) {
      navigate('/borrower/wallet/details');
    } else {
      // Default fallback
      navigate('/dashboard');
    }
  };

  const getRetryUrl = () => {
    if (isInvestor && investmentId) {
      return `/investor/investment/payment/${investmentId}`;
    } else if (isBorrower && borrowerId) {
      return `/borrower/deposit/payment/${borrowerId}`;
    } else {
      return '/dashboard';
    }
  };

  const getDetailsButtonText = () => {
    if (isInvestor && type === 'investment') {
      return 'View Investment Details';
    } else if (isInvestor) {
      return 'View Portfolio';
    } else if (isBorrower && type === 'deposit') {
      return 'View Wallet Details';
    } else if (isBorrower) {
      return 'View Wallet';
    } else {
      return 'View Details';
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader className="ninjadash-page-header-main" title={<h4>Verifying Payment</h4>} routes={PageRoutes} />
        <Main style={{ minHeight: 'auto' }}>
          <Card>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                padding: '50px 0',
              }}
            >
              <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
              <Title level={4} style={{ marginTop: 20 }}>
                Verifying your payment...
              </Title>
              <Text type="secondary">Please wait while we confirm your transaction.</Text>

              {/* Added visual indication of active processing */}
              <div className="progress-indicator" style={{ marginTop: 30, width: '60%' }}>
                <div style={{ height: 4, backgroundColor: '#f0f0f0', borderRadius: 2, overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      backgroundColor: '#1890ff',
                      borderRadius: 2,
                      animation: 'progressAnimation 1.5s infinite linear',
                      width: '30%',
                    }}
                  />
                </div>
                <style>{`
                  @keyframes progressAnimation {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(400%); }
                  }
                `}</style>
              </div>
            </div>
          </Card>
        </Main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader
          className="ninjadash-page-header-main"
          title={<h4>Payment Verification Error</h4>}
          routes={PageRoutes}
        />
        <Main style={{ minHeight: 'auto' }}>
          <Result
            status="error"
            title="Payment Verification Failed"
            subTitle={error}
            extra={[
              <Button type="primary" key="dashboard" onClick={goToDashboard}>
                Go to Dashboard
              </Button>,
              <Button key="try" onClick={() => window.location.reload()}>
                Try Again
              </Button>,
            ]}
          />
          <Card style={{ marginTop: 24 }}>
            <Text type="secondary">
              Details: The payment verification process encountered an error. This could be due to network issues,
              expired session, or issues with the payment provider. If this persists, please contact support.
            </Text>
            {orderTrackingId && (
              <div style={{ marginTop: 16 }}>
                <Text strong>Order Tracking ID: </Text>
                <Text code>{orderTrackingId}</Text>
              </div>
            )}
            {transactionId && (
              <div style={{ marginTop: 8 }}>
                <Text strong>Transaction ID: </Text>
                <Text code>{transactionId}</Text>
              </div>
            )}
          </Card>
        </Main>
      </>
    );
  }

  return (
    <>
      <PageHeader
        className="ninjadash-page-header-main"
        title={<h4>Payment {paymentResult?.success ? 'Successful' : 'Failed'}</h4>}
        routes={PageRoutes}
      />
      <Main style={{ minHeight: 'auto' }}>
        {paymentResult?.success ? (
          <Result
            icon={<CheckCircleFilled style={{ color: '#52c41a' }} />}
            status="success"
            title="Payment Successfully Processed!"
            subTitle={paymentResult.message}
            extra={[
              <Button type="primary" key="details" onClick={goToDetails}>
                {getDetailsButtonText()}
              </Button>,
              <Button key="dashboard" onClick={goToDashboard}>
                <UilLeft /> Return to Dashboard
              </Button>,
            ]}
          >
            {paymentResult.details && (
              <Card style={{ marginTop: 24 }}>
                <Descriptions title="Payment Details" bordered column={1}>
                  <Descriptions.Item label="Payment Method">
                    {paymentResult.details.method || 'Not available'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Confirmation Code">
                    {paymentResult.details.confirmation || 'Not available'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Account">
                    {paymentResult.details.account || 'Not available'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Amount">
                    {paymentResult.details.amount
                      ? `${paymentResult.details.currency || 'KES'} ${paymentResult.details.amount}`
                      : 'Not available'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date">
                    {paymentResult.details.date || new Date().toLocaleString()}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}
          </Result>
        ) : (
          <Result
            icon={<CloseCircleFilled style={{ color: '#ff4d4f' }} />}
            status="error"
            title="Payment Failed"
            subTitle={paymentResult?.message || 'Your payment could not be processed. Please try again.'}
            extra={[
              <Button type="primary" danger key="retry" onClick={() => navigate(getRetryUrl())}>
                Try Again
              </Button>,
              <Button key="dashboard" onClick={goToDashboard}>
                <UilLeft /> Return to Dashboard
              </Button>,
            ]}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Divider />
                <Text type="secondary">
                  If you continue to experience issues with your payment, please contact our support team for
                  assistance.
                </Text>
              </Col>
            </Row>
          </Result>
        )}
      </Main>
    </>
  );
}

export default PaymentSuccessPage;
